import { useState, useEffect } from "react";
import {
  Card, Button, Modal, Form, Input, Select, Upload,
  Tag, Typography, Space, message, Divider
} from "antd";
import { UploadOutlined, TrophyOutlined, InstagramOutlined, GlobalOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../Config";

const { Text, Title } = Typography;

const TIERS = [
  { min: 1_000_000, label: ">1M followers",   bonusDays: 300, bonusDaysLogo: 240 },
  { min: 100_000,   label: ">100k followers",  bonusDays: 180, bonusDaysLogo: 120 },
  { min: 50_000,    label: ">50k followers",   bonusDays: 90,  bonusDaysLogo: 60  },
  { min: 10_000,    label: ">10k followers",   bonusDays: 30,  bonusDaysLogo: 20  },
  { min: 1_000,     label: ">1k followers",    bonusDays: 15,  bonusDaysLogo: 10  },
];

const PLATFORMS = ["Instagram", "YouTube", "Facebook", "TikTok", "Twitter/X", "LinkedIn", "Other"];

function getTier(count) {
  return TIERS.find(t => count >= t.min) ?? null;
}

function StatusBadge({ status }) {
  const map = {
    pending:  { color: "orange",  text: "Pending Review" },
    approved: { color: "green",   text: "Approved ✓" },
    rejected: { color: "red",     text: "Rejected" },
  };
  const s = map[status] ?? { color: "default", text: status };
  return <Tag color={s.color} style={{ borderRadius: 20, padding: "2px 12px" }}>{s.text}</Tag>;
}

export default function FollowerBonus() {
  const [claim, setClaim]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [followerCount, setFollowerCount] = useState("");
  const [withLogo, setWithLogo]   = useState(false);
  const [fileList, setFileList]   = useState([]);
  const [form]                    = Form.useForm();

  const token = sessionStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchClaim(); }, []);

  async function fetchClaim() {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/follower-bonus/my-claim`, { headers });
      setClaim(res.data.claim);
    } catch { /* no claim yet */ }
    finally { setLoading(false); }
  }

  const previewTier = getTier(Number(followerCount));

  async function handleSubmit(values) {
    if (!fileList[0]) return message.error("Please upload a screenshot");

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("socialMediaUrl",  values.socialMediaUrl);
      fd.append("platform",        values.platform);
      fd.append("followerCount",   values.followerCount);
      fd.append("withLogo",        values.withLogo ?? false);
      fd.append("screenshot",      fileList[0].originFileObj);

      await axios.post(`${BASE_URL}/follower-bonus/claim`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });

      message.success("Claim submitted! Admin will review within 24–48 hours.");
      setModalOpen(false);
      form.resetFields();
      setFileList([]);
      setFollowerCount("");
      fetchClaim();
    } catch (err) {
      message.error(err.response?.data?.message ?? "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render existing claim ─────────────────────────────────────
  if (!loading && claim) {
    const tier = getTier(claim.followerCount);
    const bonus = claim.withLogo ? tier?.bonusDaysLogo : tier?.bonusDays;

    return (
      <Card
        style={{ borderRadius: 16, border: "1px solid #E5E7EB", marginTop: 16 }}
        bodyStyle={{ padding: 24 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Title level={5} style={{ margin: 0 }}>
            <TrophyOutlined style={{ color: "#f59e0b", marginRight: 8 }} />
            Follower Bonus
          </Title>
          <StatusBadge status={claim.status} />
        </div>

        <div style={{ background: "#FAFAFA", borderRadius: 10, padding: 16, border: "1px solid #F3F4F6" }}>
          {[
            { label: "Platform",       value: claim.platform },
            { label: "Followers",      value: Number(claim.followerCount).toLocaleString() },
            { label: "Bonus",          value: bonus ? `+${bonus} days` : "—" },
            { label: "With Logo",      value: claim.withLogo ? "Yes" : "No" },
            { label: "Submitted",      value: new Date(claim.createdAt).toLocaleDateString() },
          ].map(({ label, value }) => (
            <div key={label} style={{
              display: "flex", justifyContent: "space-between",
              padding: "6px 0", borderBottom: "1px solid #F3F4F6",
            }}>
              <Text type="secondary" style={{ fontSize: 13 }}>{label}</Text>
              <Text strong style={{ fontSize: 13 }}>{value}</Text>
            </div>
          ))}
        </div>

        {claim.status === "rejected" && (
          <Button
            block type="primary" style={{ marginTop: 14, background: "#4f46e5" }}
            onClick={() => { setClaim(null); setModalOpen(true); }}
          >
            Submit New Claim
          </Button>
        )}
      </Card>
    );
  }

  // ── No claim yet ──────────────────────────────────────────────
  return (
    <>
      <Card
        style={{ borderRadius: 16, border: "1px solid #E5E7EB", marginTop: 16 }}
        bodyStyle={{ padding: 24 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            <TrophyOutlined style={{ color: "#f59e0b", marginRight: 8 }} />
            Follower Bonus
          </Title>
        </div>

        {/* Tiers preview table */}
        <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #F3F4F6", marginBottom: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#F9FAFB" }}>
                {["Followers", "Bonus Days", "With Logo"].map(h => (
                  <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontSize: 11,
                    fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TIERS.map((t, i) => (
                <tr key={t.min} style={{ borderTop: i > 0 ? "1px solid #F3F4F6" : "none" }}>
                  <td style={{ padding: "10px 14px", color: "#111827", fontWeight: 600 }}>{t.label}</td>
                  <td style={{ padding: "10px 14px", color: "#059669", fontWeight: 500 }}>+{t.bonusDays} days</td>
                  <td style={{ padding: "10px 14px", color: "#6B7280" }}>+{t.bonusDaysLogo} days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 14 }}>
          Bonus is added on top of your existing extension. Requires admin verification.
        </Text>

        <Button
          block size="large" type="primary"
          style={{ background: "#4f46e5", borderColor: "#4f46e5", borderRadius: 8 }}
          onClick={() => setModalOpen(true)}
          loading={loading}
        >
          Claim Follower Bonus
        </Button>
      </Card>

      {/* ── Submit Modal ── */}
      <Modal
        title={<><TrophyOutlined style={{ color: "#f59e0b", marginRight: 8 }} />Claim Follower Bonus</>}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); setFollowerCount(""); setFileList([]); }}
        footer={null}
        borderRadius={16}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 12 }}>

          <Form.Item label="Social Media Platform" name="platform" rules={[{ required: true }]}>
            <Select placeholder="Select platform">
              {PLATFORMS.map(p => <Select.Option key={p} value={p}>{p}</Select.Option>)}
            </Select>
          </Form.Item>

          <Form.Item label="Profile URL" name="socialMediaUrl" rules={[{ required: true, type: "url", message: "Enter a valid URL" }]}>
            <Input prefix={<GlobalOutlined />} placeholder="https://instagram.com/yourprofile" />
          </Form.Item>

          <Form.Item label="Follower Count" name="followerCount" rules={[{ required: true }]}>
            <Input
              type="number" min={1000}
              placeholder="e.g. 15000"
              prefix={<InstagramOutlined />}
              onChange={e => setFollowerCount(e.target.value)}
            />
          </Form.Item>

          {/* Live tier preview */}
          {previewTier && (
            <div style={{
              background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8,
              padding: "10px 14px", marginBottom: 16,
            }}>
              <Text style={{ color: "#16A34A", fontWeight: 600 }}>
                ✓ Qualifies: {previewTier.label} → +{withLogo ? previewTier.bonusDaysLogo : previewTier.bonusDays} days bonus
              </Text>
            </div>
          )}

          <Form.Item label="Do you have a logo on your images?" name="withLogo" initialValue={false}>
            <Select onChange={v => setWithLogo(v)}>
              <Select.Option value={false}>No — Media link only</Select.Option>
              <Select.Option value={true}>Yes — With logo</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Screenshot of follower count" required>
            <Upload
              beforeUpload={() => false}
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList.slice(-1))}
              accept="image/*"
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>Upload Screenshot</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: "block" }}>
              Screenshot must clearly show your follower count and profile name.
            </Text>
          </Form.Item>

          <Divider style={{ margin: "12px 0" }} />

          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}
              style={{ background: "#4f46e5", borderColor: "#4f46e5" }}>
              Submit Claim
            </Button>
          </Space>
        </Form>
      </Modal>
    </>
  );
}